import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";

/**
 * Marquee — Infinite horizontal scrolling band with luxury controls.
 *
 * Props:
 * - items: Array<{ slug: string, name: string } | string>
 * - speed: number    — seconds for one full cycle (default 50)
 * - reverse: bool    — scroll right-to-left if false (default), left-to-right if true
 * - variant: "primary" (default) | "secondary"
 * - testid: string
 */
export default function Marquee({
    items,
    speed = 50,
    reverse = false,
    variant = "primary",
    testid = "marquee",
}) {
    const [paused, setPaused] = useState(false);
    const [offset, setOffset] = useState(0);

    // Normalize: every item becomes { slug, name }
    const normalized = items.map((it) =>
        typeof it === "string" ? { slug: null, name: it } : it
    );

    // Duplicate items so the loop is seamless
    const sequence = [...normalized, ...normalized];

    const isSecondary = variant === "secondary";

    const baseItemCls = isSecondary
        ? "group inline-flex items-center shrink-0 px-5 md:px-8 text-base md:text-xl tracking-[0.22em] uppercase font-medium font-body transition-colors duration-200"
        : "group inline-flex items-center shrink-0 px-6 md:px-10 text-2xl md:text-4xl font-display tracking-[-0.01em] transition-colors duration-200";

    const diamondCls = isSecondary
        ? "ml-5 md:ml-8 inline-block w-1.5 h-1.5 rotate-45 bg-[var(--crewz-text)]/40"
        : "ml-6 md:ml-10 inline-block w-2 h-2 rotate-45 bg-[var(--crewz-text)]/40";

    const nudge = (dir) => {
        // Auto-pause when user takes manual control
        setPaused(true);
        // Clamp offset to ±1 viewport so user can't translate into blank space.
        const maxOffset = typeof window !== "undefined" ? window.innerWidth : 1600;
        setOffset((o) => {
            const next = o + (dir === "left" ? 320 : -320);
            return Math.max(-maxOffset, Math.min(maxOffset, next));
        });
    };

    const togglePause = () => {
        setPaused((p) => !p);
        // Don't reset offset on toggle — preserve user's manual nudge
    };

    return (
        <div
            className={`relative overflow-hidden ${
                isSecondary ? "py-4 md:py-5 border-t border-white/5" : "py-7 md:py-9 border-y border-white/10"
            } bg-[var(--crewz-bg-2)]`}
            data-testid={testid}
        >
            {/* Edge fades */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-24 md:w-40 bg-gradient-to-r from-[var(--crewz-bg-2)] to-transparent z-10" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-24 md:w-40 bg-gradient-to-l from-[var(--crewz-bg-2)] to-transparent z-10" />

            {/* Manual nudge — left */}
            <button
                type="button"
                onClick={() => nudge("left")}
                aria-label="Scroll left"
                data-testid={`${testid}-arrow-left`}
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 grid place-items-center bg-[var(--crewz-bg)]/70 hover:bg-[var(--crewz-gold)] hover:text-[var(--crewz-bg)] text-[var(--crewz-text)] border border-white/10 backdrop-blur-sm transition-all duration-200 rounded-full"
            >
                <ChevronLeft size={isSecondary ? 16 : 18} strokeWidth={1.5} />
            </button>

            {/* Pause / Play */}
            <button
                type="button"
                onClick={togglePause}
                aria-label={paused ? "Resume scroll" : "Pause scroll"}
                data-testid={`${testid}-pause-toggle`}
                className="absolute right-14 md:right-20 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 grid place-items-center bg-[var(--crewz-bg)]/70 hover:bg-[var(--crewz-gold)] hover:text-[var(--crewz-bg)] text-[var(--crewz-text)] border border-white/10 backdrop-blur-sm transition-all duration-200 rounded-full"
            >
                {paused ? <Play size={isSecondary ? 14 : 16} strokeWidth={1.5} /> : <Pause size={isSecondary ? 14 : 16} strokeWidth={1.5} />}
            </button>

            {/* Manual nudge — right */}
            <button
                type="button"
                onClick={() => nudge("right")}
                aria-label="Scroll right"
                data-testid={`${testid}-arrow-right`}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 grid place-items-center bg-[var(--crewz-bg)]/70 hover:bg-[var(--crewz-gold)] hover:text-[var(--crewz-bg)] text-[var(--crewz-text)] border border-white/10 backdrop-blur-sm transition-all duration-200 rounded-full"
            >
                <ChevronRight size={isSecondary ? 16 : 18} strokeWidth={1.5} />
            </button>

            {/* Manual offset wrapper — independent of CSS animation transform */}
            <div
                style={{
                    transform: `translate3d(${offset}px, 0, 0)`,
                    transition: "transform 600ms cubic-bezier(0.22, 0.61, 0.36, 1)",
                }}
            >
                <div
                    className="marquee-track flex shrink-0 items-center"
                    style={{
                        animationDuration: `${speed}s`,
                        animationDirection: reverse ? "reverse" : "normal",
                        animationPlayState: paused ? "paused" : "running",
                    }}
                >
                    {sequence.map((item, i) => {
                        const isGold = i % 2 === 1;
                        const isClone = i >= normalized.length;
                        const color = isGold
                            ? "text-[var(--crewz-gold)] hover:text-[var(--crewz-gold-soft)]"
                            : "text-[var(--crewz-text)] hover:text-[var(--crewz-gold)]";

                        const body = (
                            <>
                                <span className="relative">
                                    {item.name}
                                    <span
                                        aria-hidden
                                        className="absolute left-0 -bottom-1 h-px bg-current w-0 group-hover:w-full transition-all duration-300"
                                    />
                                </span>
                                <span aria-hidden className={diamondCls} />
                            </>
                        );

                        if (item.slug) {
                            return (
                                <Link
                                    key={`${item.slug}-${i}`}
                                    to={`/builds/${item.slug}`}
                                    className={`${baseItemCls} ${color}`}
                                    data-testid={`marquee-item-${item.slug}${isClone ? "-clone" : ""}`}
                                >
                                    {body}
                                </Link>
                            );
                        }
                        return (
                            <span key={i} className={`${baseItemCls} ${color} cursor-default`}>
                                {body}
                            </span>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
